import * as THREE from 'three';

export default class DebugManager {
    constructor(scene, physics, camera) {
        this.scene = scene;
        this.physics = physics;
        this.camera = camera;

        // Debug state
        this.debugMode = false;
        this.debugInfo = null;
        this.collisionHelpers = [];

        // Performance monitoring
        this.stats = {
            fps: 0,
            frameTime: 0,
            frames: 0,
            lastCheck: performance.now()
        };
    }

    get isDebugMode() { return this.debugMode; }
    getStats() { return this.stats; }

    setup() {
        this.debugInfo = document.createElement('div');
        this.debugInfo.id = 'debug-info';
        this.debugInfo.style.display = 'none';
        this.debugInfo.style.position = 'fixed';
        this.debugInfo.style.top = '10px';
        this.debugInfo.style.left = '10px';
        this.debugInfo.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
        this.debugInfo.style.color = 'white';
        this.debugInfo.style.padding = '10px';
        this.debugInfo.style.borderRadius = '5px';
        this.debugInfo.style.fontFamily = 'monospace';
        this.debugInfo.style.fontSize = '12px';
        this.debugInfo.style.zIndex = '1000';
        document.body.appendChild(this.debugInfo);
    }

    toggle() {
        this.debugMode = !this.debugMode;

        if (this.debugInfo) {
            this.debugInfo.style.display = this.debugMode ? 'block' : 'none';
        }

        if (this.physics && this.physics.isInitialized) {
            this.physics.setDebugMode(this.debugMode);
        }

        this.toggleCollisionVisualization(this.debugMode);
    }

    toggleCollisionVisualization(show) {
        // Remove existing helpers
        if (this.collisionHelpers) {
            this.collisionHelpers.forEach(helper => this.scene.remove(helper));
            this.collisionHelpers = [];
        }

        if (!show) return;

        this.collisionHelpers = [];

        if (this.physics && this.physics.bodies) {
            this.physics.bodies.forEach(body => {
                if (body.type === 'box') {
                    const boxGeometry = new THREE.BoxGeometry(
                        body.halfExtents.x * 2,
                        body.halfExtents.y * 2,
                        body.halfExtents.z * 2
                    );
                    const boxMaterial = new THREE.MeshBasicMaterial({
                        color: 0xff0000,
                        wireframe: true,
                        opacity: 0.6,
                        transparent: true
                    });

                    const boxHelper = new THREE.Mesh(boxGeometry, boxMaterial);
                    boxHelper.position.copy(body.position);
                    boxHelper.quaternion.copy(body.quaternion);

                    this.scene.add(boxHelper);
                    this.collisionHelpers.push(boxHelper);

                    if (body.visualMesh || body.visualMeshes) {
                        boxHelper.userData.linkedBody = body;
                    }
                }
            });
        }
    }

    updateCollisionVisualization() {
        if (!this.collisionHelpers || !this.physics) return;

        this.collisionHelpers.forEach(helper => {
            if (helper.userData.linkedBody) {
                helper.position.copy(helper.userData.linkedBody.position);
                helper.quaternion.copy(helper.userData.linkedBody.quaternion);
            }
        });
    }

    updateStats() {
        this.stats.frames++;
        const now = performance.now();
        const elapsed = now - this.stats.lastCheck;

        if (elapsed > 1000) {
            this.stats.fps = Math.round((this.stats.frames * 1000) / elapsed);
            this.stats.frameTime = elapsed / this.stats.frames;
            this.stats.frames = 0;
            this.stats.lastCheck = now;
        }
    }

    updateDebugInfo(bus, gasStations) {
        if (!this.debugMode || !bus) return;

        const fps = this.stats.fps;
        const frameTime = this.stats.frameTime.toFixed(2);

        const speed = bus.getSpeed();
        const position = bus.getPosition();

        const fuelLevel = bus.fuel ? bus.fuel.toFixed(1) : 0;
        const fuelPercentage = bus.fuel && bus.fuelCapacity ?
            (bus.fuel / bus.fuelCapacity * 100).toFixed(1) : 0;

        let nearestStationInfo = 'None in range';
        if (gasStations && gasStations.length > 0) {
            let nearestDistance = Infinity;
            let nearestStation = null;

            for (const station of gasStations) {
                const distanceToStation = position.distanceTo(
                    new THREE.Vector3(station.position.x, 0, station.position.z)
                );

                if (distanceToStation < nearestDistance) {
                    nearestDistance = distanceToStation;
                    nearestStation = station;
                }
            }

            if (nearestStation) {
                nearestStationInfo = `${nearestStation.name} (${nearestDistance.toFixed(1)} units away)`;
            }
        }

        this.debugInfo.innerHTML = `
            <h3>Performance</h3>
            <div>FPS: ${fps}</div>
            <div>Frame Time: ${frameTime} ms</div>

            <h3>Bus Data</h3>
            <div>Speed: ${(speed * 3.6).toFixed(1)} km/h</div>
            <div>Position: (${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)})</div>
            <div>Fuel: ${fuelLevel} L (${fuelPercentage}%)</div>

            <h3>Gas Stations</h3>
            <div>Count: ${gasStations ? gasStations.length : 0}</div>
            <div>Nearest: ${nearestStationInfo}</div>
        `;
    }

    showDebugHelpers(value) {
        if (this.physics?.debugRenderer) {
            this.physics.debugRenderer.visible = value;
        }
    }
}
